import { memberRegistrationSpecs } from "../member/memberFields";
import { fieldsFromSchema } from "./readSchemaFields";
import { packageVoiceSchema } from "../package/voiceAddSchema";
import { staffVoiceSchema } from "../staff/voiceAddSchema";
import { gateVoiceSchema } from "../gate/voiceAddSchema";
import { courtVoiceSchema } from "../court/voiceAddSchema";
import { followupVoiceSchema } from "../followupProgram/voiceAddSchema";
import { trainerVoiceSchema } from "../trainer/voiceAddSchema";
import { wodVoiceSchema } from "../wod/voiceAddSchema";
import { leadVoiceSchema } from "../lead/voiceAddSchema";
import { retailVoiceSchema } from "../retail/voiceAddSchema";
import { saleVoiceSchema } from "../sale/voiceAddSchema";
import { classVoiceSchema } from "../class/voiceAddSchema";
import { workshopVoiceSchema } from "../class/workshopVoiceSchema";
import { AppointmentSchema } from "../appointment/utils";
import { buildContractCatalog } from "./contracts";
import {
  applyAppointmentVoiceFields,
  applyPackageVoiceFields,
  applyScheduledVoiceFields,
} from "./voiceFieldDefaults";

const formInput = (field) =>
  /image|picture|photo|file/i.test(field.key)
    ? { ...field, input: "form" }
    : field;

const fromSchema = (schema) =>
  withVoiceBehavior(fieldsFromSchema(schema).map(formInput));

const withVoiceBehavior = (fields) =>
  fields.map((field) => {
    if (field.key === "gender" || field.key.endsWith(".gender")) {
      return { ...field, infer: "name" };
    }
    return field;
  });

const LOOKUP_BY_KEY = {
  packages: { source: "package", multiple: true },
  assignedPackages: { source: "package", multiple: true },
  products: { source: "product", multiple: true },
  trainer: { source: "instructor" },
  trainers: { source: "instructor", multiple: true },
  member: { source: "member" },
  members: { source: "member", multiple: true },
  class: { source: "class" },
  classes: { source: "class", multiple: true },
  appointment: { source: "appointment" },
  appointments: { source: "appointment", multiple: true },
};

const withLookups = (action) => {
  let fields = action.fields
    .filter((field) => field.key !== "selectedSaleServices")
    .map((field) => {
      const spec = LOOKUP_BY_KEY[field.key];
      if (!spec) return field;
      return { ...field, lookup: spec.source, multiple: Boolean(spec.multiple) };
    });
  const groups = (action.groups || []).slice();
  if (action.type === "sale") {
    fields = [
      {
        key: "member",
        label: "Member",
        required: true,
        lookup: "member",
      },
      ...fields,
    ];
    const keys = fields.map((field) => field.key);
    if (keys.includes("packages") && keys.includes("products")) {
      groups.push({
        keys: ["packages", "products"],
        label: "Package or product",
      });
    }
  }
  return { ...action, fields, groups };
};

const saleDefaults = (fields, taxRate) =>
  fields.map((field) => {
    if (field.key === "paymentStatus") {
      return {
        ...field,
        default: "F",
        options: [
          { value: "F", aliases: ["Full payment", "full", "كامل"] },
          { value: "P", aliases: ["Partial payment", "partial", "جزئي"] },
        ],
      };
    }
    if (field.key === "paymentType") {
      return {
        ...field,
        default: "L",
        options: [
          { value: "L", aliases: ["Cash", "cash", "كاش"] },
          { value: "CC", aliases: ["Visa", "card", "فيزا"] },
          { value: "CQ", aliases: ["Check", "cheque", "شيك"] },
          { value: "CO", aliases: ["Complimentary", "مجاني"] },
        ],
      };
    }
    if (field.key === "tax") return { ...field, default: taxRate };
    return field;
  });

const blocked = (code, access) => {
  if (
    access.role === "STAFF" &&
    access.subAdminType !== "SUB_ADMIN" &&
    !access.permissions[code]
  ) {
    return true;
  }
  if (access.subAdminType === "SUB_ADMIN" && !access.subAdminPermissions[code]) {
    return true;
  }
  return false;
};

const membershipOn = (gymConfig) => {
  const memberships = gymConfig && gymConfig.manageMemberships;
  if (!memberships || !memberships.enabled) return false;
  return Boolean(
    (memberships.gymMemberships && memberships.gymMemberships.enabled) ||
      (memberships.classMemberships && memberships.classMemberships.enabled) ||
      (memberships.appointmentMemberships &&
        memberships.appointmentMemberships.enabled) ||
      (memberships.workshopAndCoursesMemberships &&
        memberships.workshopAndCoursesMemberships.enabled)
  );
};

const serviceBlocked = (access) =>
  access.role === "TRAINER" || blocked("S-SERV-PROD", access);

const appointmentAllowed = (access) => {
  if (access.role === "STAFF" && access.subAdminType !== "SUB_ADMIN") {
    return Boolean(access.permissions["S-SERV-PROD"]);
  }
  if (access.subAdminType === "SUB_ADMIN") {
    return Boolean(access.subAdminPermissions["S-SERV-PROD"]);
  }
  return access.role === "GYM";
};

const courtAllowed = (access) => {
  if (
    access.role === "STAFF" &&
    access.subAdminType !== "SUB_ADMIN" &&
    !access.permissions["S-COURT-ADD"]
  ) {
    return false;
  }
  if (access.subAdminType === "SUB_ADMIN" && !access.subAdminPermissions["S-COURT-ADD"]) {
    return false;
  }
  return true;
};

const gatesAllowed = (access) => {
  if (!access.role || access.role === "STAFF") {
    return Boolean(
      access.permissions["S-GATES-FEAT"] || access.subAdminPermissions["S-GATES-FEAT"]
    );
  }
  return true;
};

function legacyBuildVoiceCatalog({
  gym,
  gymConfig,
  role,
  loggedInRole,
  permissions,
  subAdminType,
  subAdminPermissions,
}) {
  const access = {
    role: role || "",
    loggedInRole: loggedInRole || "",
    permissions: permissions || {},
    subAdminType: subAdminType || "",
    subAdminPermissions: subAdminPermissions || {},
  };
  const config = gymConfig || {};
  const type = (gym && gym.type) || "";
  const actions = [];
  const memberBlock =
    access.loggedInRole === "PT" ||
    access.role === "GYM" ||
    access.role === "STAFF" ||
    access.subAdminType === "SUB_ADMIN";
  const staffBlock =
    access.role === "GYM" ||
    access.role === "STAFF" ||
    access.subAdminType === "SUB_ADMIN";

  if (
    config.manageMemberships &&
    config.manageMemberships.enabled &&
    config.manageMemberships.classMemberships &&
    config.manageMemberships.classMemberships.enabled &&
    !serviceBlocked(access)
  ) {
    actions.push({
      type: "class",
      label: type === "sportClub" ? "Add Group Session" : "Add Class",
      fields: applyScheduledVoiceFields(fromSchema(classVoiceSchema), "class"),
    });
  }

  if (
    config.manageMemberships &&
    config.manageMemberships.enabled &&
    config.manageMemberships.workshopAndCoursesMemberships &&
    config.manageMemberships.workshopAndCoursesMemberships.enabled &&
    !serviceBlocked(access)
  ) {
    actions.push({
      type: "workshop",
      label: "Add Workshop",
      fields: applyScheduledVoiceFields(
        fromSchema(workshopVoiceSchema),
        "workshop"
      ),
    });
  }

  if (type === "sportClub" && courtAllowed(access)) {
    actions.push({
      type: "court",
      label: "Add Court",
      fields: fromSchema(courtVoiceSchema),
    });
  }
  if (type === "complex" && courtAllowed(access)) {
    actions.push({
      type: "court",
      label: "Add Room",
      fields: fromSchema(courtVoiceSchema),
    });
  }

  if (memberBlock && config.manageMembers && config.manageMembers.enabled && !blocked("S-M-ADD", access)) {
    const registration =
      (gym && gym.settings && gym.settings.memberRegistration) || {};
    const specs = memberRegistrationSpecs((gym && gym.settings) || {}, config);
    const genderDefault =
      registration.genderDefaultValue === "M" ||
      registration.genderDefaultValue === "F"
        ? registration.genderDefaultValue
        : "";
    actions.push({
      type: "member",
      label: "Add Member",
      fields: specs.fields.map((field) => {
        const next = formInput(field);
        if (next.key === "gender") {
          return {
            ...next,
            infer: "name",
            ...(genderDefault ? { default: genderDefault } : {}),
          };
        }
        if (next.key === "image") {
          return { ...next, input: "value", assumed: "placeholder" };
        }
        if (next.key === "RFIDCardIdField") return { ...next, input: "scan" };
        return next;
      }),
      groups: specs.groups.map((group) => ({
        keys: group.keys,
        label: group.label,
      })),
    });
  }

  if (memberBlock && config.leadManagement && config.leadManagement.enabled) {
    actions.push({
      type: "lead",
      label: "Add Lead",
      fields: withVoiceBehavior(fromSchema(leadVoiceSchema)),
    });
  }

  if (
    memberBlock &&
    ((config.manageMemberships && config.manageMemberships.enabled) ||
      (config.retail && config.retail.enabled)) &&
    !(
      access.role === "STAFF" &&
      access.subAdminType !== "SUB_ADMIN" &&
      !access.permissions["S-SAL-ADD"] &&
      !access.permissions["S-SAL-PRO"]
    ) &&
    !(
      access.subAdminType === "SUB_ADMIN" &&
      !access.subAdminPermissions["S-SAL-ADD"] &&
      !access.subAdminPermissions["S-SAL-PRO"]
    )
  ) {
    actions.push({
      type: "sale",
      label: "Add Sale",
      fields: saleDefaults(
        withVoiceBehavior(fromSchema(saleVoiceSchema)),
        gym && gym.taxRate !== undefined && gym.taxRate !== null && gym.taxRate !== ""
          ? String(gym.taxRate)
          : "0"
      ),
    });
  }

  if (memberBlock && membershipOn(config) && !blocked("S-PAC-ADD/D", access)) {
    actions.push({
      type: "package",
      label: "Add Package",
      fields: applyPackageVoiceFields(fromSchema(packageVoiceSchema), {
        gymType: type,
        gym,
        config,
      }),
    });
  }

  if (memberBlock && config.retail && config.retail.enabled && !blocked("S-ADD-PRO", access)) {
    actions.push({
      type: "product",
      label: type === "sportClub" ? "Add Service" : "Add Product",
      fields: fromSchema(retailVoiceSchema),
    });
  }

  if (!staffBlock) return actions;

  if (config.manageInstructors && config.manageInstructors.enabled && !blocked("S-I-ADD", access)) {
    actions.push({
      type: "instructor",
      label: type === "sportClub" ? "Add Coach" : "Add Instructor",
      fields: fromSchema(trainerVoiceSchema),
    });
  }

  if (
    config.manageMemberships &&
    config.manageMemberships.enabled &&
    config.manageMemberships.appointmentMemberships &&
    config.manageMemberships.appointmentMemberships.enabled &&
    appointmentAllowed(access)
  ) {
    actions.push({
      type: "appointment",
      label: type === "sportClub" ? "Add Private Lesson" : "Add Appointment",
      fields: applyAppointmentVoiceFields(fromSchema(AppointmentSchema)),
    });
  }

  if (config.manageStaff && config.manageStaff.enabled && !blocked("S-S-MNG", access)) {
    actions.push({
      type: "staff",
      label: "Add Staff",
      fields: fromSchema(staffVoiceSchema),
    });
  }

  if (config.workouts && config.workouts.enabled && !blocked("S-W-ADD", access)) {
    actions.push({
      type: "workout",
      label: "Add Workout",
      fields: fromSchema(wodVoiceSchema),
    });
  }

  if (config.followupProgram && config.followupProgram.enabled) {
    actions.push({
      type: "followup",
      label: "Add Follow-up Program",
      fields: fromSchema(followupVoiceSchema),
    });
  }

  if (config.gates && config.gates.enabled && gatesAllowed(access)) {
    actions.push({
      type: "gate",
      label: "Add Gate",
      fields: fromSchema(gateVoiceSchema),
    });
  }

  return actions.map(withLookups);
}

export function buildVoiceCatalog(context) {
  return buildContractCatalog(context);
}

export { legacyBuildVoiceCatalog };
